// StudyDost — Homework Generator Page
// frontend/src/pages/HomeworkGenerator.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://studydost-backend-production.up.railway.app'; // set REACT_APP_API_URL in env

const TEMPLATES = [
  { id: 'board_style',    label: 'Board Style',    desc: '5 MCQ + 3 Short + 2 Long', icon: '📋' },
  { id: 'quick_quiz',     label: 'Quick Quiz',     desc: '5 MCQ + 3 Short',          icon: '⚡' },
  { id: 'practice_test',  label: 'Practice Test',  desc: '10 MCQ + 5 Short + 2 Long', icon: '📝' },
  { id: 'revision_sheet', label: 'Revision Sheet', desc: '5 Short + 3 Long',          icon: '🔄' },
];

const SUBJECTS = [
  'Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology',
  'English', 'Hindi', 'Social Science', 'History', 'Geography',
  'Civics', 'Economics', 'Computer Science', 'Sanskrit', 'EVS',
];

const CLASSES = ['Class 1','Class 2','Class 3','Class 4','Class 5','Class 6',
  'Class 7','Class 8','Class 9','Class 10','Class 11','Class 12'];

const BOARDS = ['CBSE','ICSE','BSEB','UP Board','MP Board','Rajasthan Board','Other'];

export default function HomeworkGenerator({ token, teacherProfile }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form | generating | review | done
  const [form, setForm] = useState({
    subject: '', chapter: '', classLevel: '', board: teacherProfile?.board || 'CBSE',
    language: 'ENGLISH', template: 'board_style',
  });
  const [homeworkId, setHomeworkId] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editQuestions, setEditQuestions] = useState(null);

  const isPaid = ['PRO', 'PREMIUM', 'PAID'].includes(teacherProfile?.teacherPlan);

  const generate = async () => {
    setError(null);
    if (!form.subject || !form.chapter || !form.classLevel) {
      setError('Please fill Subject, Chapter, and Class.');
      return;
    }
    setStep('generating');
    try {
      const res = await fetch(`${API}/api/teacher/homework/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      let data = {};
      try { data = await res.json(); } catch(e) {}
      if (!res.ok) {
        if (data.error === 'FREE_LIMIT_REACHED') {
          setError('Free plan: 1 homework per day. Upgrade for unlimited!');
          setStep('form');
          return;
        }
        throw new Error(data.error || 'Generation failed');
      }
      setHomeworkId(data.homeworkId);
      setQuestions(data.questions);
      setEditQuestions(JSON.parse(JSON.stringify(data.questions)));
      setStep('review');
    } catch (e) {
      setError(e.message);
      setStep('form');
    }
  };

  const saveEdits = async () => {
    try {
      const saveRes = await fetch(`${API}/api/teacher/homework/${homeworkId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ questions: editQuestions }),
      });
      if (!saveRes.ok) throw new Error('Save failed');
      setQuestions(editQuestions);
      setEditMode(false);
    } catch (e) {
      setError('Failed to save edits');
    }
  };

  const downloadPdf = () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // Navigate directly — window.open blocked on mobile
      navigate(`/teacher/homework/print/${homeworkId}`);
    } else {
      window.open(`/teacher/homework/print/${homeworkId}`, '_blank');
    }
  };

  // ── Styles ─────────────────────────────────
  const styles = {
    page: { minHeight: '100vh', background: '#f0f4ff', padding: '24px 16px', fontFamily: "'Segoe UI', sans-serif" },
    card: { background: 'white', borderRadius: 16, padding: 28, maxWidth: 720, margin: '0 auto', boxShadow: '0 4px 24px rgba(30,136,229,0.1)' },
    title: { fontSize: 22, fontWeight: 700, color: '#1565C0', marginBottom: 4 },
    sub: { fontSize: 13, color: '#757575', marginBottom: 24 },
    label: { fontSize: 13, fontWeight: 600, color: '#424242', marginBottom: 6, display: 'block' },
    input: { width: '100%', padding: '10px 14px', border: '1.5px solid #E0E0E0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
    select: { width: '100%', padding: '10px 14px', border: '1.5px solid #E0E0E0', borderRadius: 8, fontSize: 14, background: 'white', boxSizing: 'border-box' },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
    templateGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 },
    templateCard: (selected) => ({
      border: `2px solid ${selected ? '#1E88E5' : '#E0E0E0'}`,
      background: selected ? '#E3F2FD' : 'white',
      borderRadius: 10, padding: '12px 14px', cursor: 'pointer', transition: 'all 0.15s',
    }),
    btnPrimary: { background: '#1E88E5', color: 'white', border: 'none', borderRadius: 10, padding: '13px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%' },
    btnSecondary: { background: 'white', color: '#1E88E5', border: '2px solid #1E88E5', borderRadius: 10, padding: '11px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
    btnSuccess: { background: '#2E7D32', color: 'white', border: 'none', borderRadius: 10, padding: '13px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
    sectionHead: (color) => ({ background: color, color: 'white', padding: '10px 16px', borderRadius: 8, fontWeight: 700, fontSize: 14, marginBottom: 12, marginTop: 20 }),
    qBlock: { border: '1px solid #E0E0E0', borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: '#FAFAFA' },
    qText: { fontSize: 14, color: '#212121', lineHeight: 1.5 },
    optText: { fontSize: 13, color: '#424242', marginLeft: 16, marginTop: 4 },
    marks: { fontSize: 12, color: '#1E88E5', fontWeight: 700, float: 'right' },
    errorBox: { background: '#FFEBEE', border: '1px solid #EF9A9A', borderRadius: 8, padding: '12px 16px', color: '#C62828', marginBottom: 16, fontSize: 14 },
    generating: { textAlign: 'center', padding: '60px 20px' },
    spinner: { width: 48, height: 48, border: '4px solid #E3F2FD', borderTop: '4px solid #1E88E5', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' },
    brandBadge: { background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: 20, padding: '4px 12px', fontSize: 12, color: '#1565C0', display: 'inline-block', marginTop: 8 },
  };

  if (step === 'generating') return (
    <div style={styles.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.card}>
        <div style={styles.generating}>
          <div style={styles.spinner} />
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1565C0', marginBottom: 8 }}>✨ Generating Homework...</div>
          <div style={{ color: '#757575', fontSize: 14 }}>
            AI is creating {form.subject} questions for {form.classLevel}<br />
            This takes 10-15 seconds...
          </div>
        </div>
      </div>
    </div>
  );

  if (step === 'review' && questions) return (
    <div style={styles.page}>
      <div style={{ ...styles.card, maxWidth: 800 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={styles.title}>📝 Review Homework</div>
            <div style={styles.sub}>{form.subject} · {form.classLevel} · {form.chapter}</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {!editMode ? (
              <button style={styles.btnSecondary} onClick={() => setEditMode(true)}>✏️ Edit</button>
            ) : (
              <>
                <button style={styles.btnSecondary} onClick={() => { setEditMode(false); setEditQuestions(JSON.parse(JSON.stringify(questions))); }}>Cancel</button>
                <button style={{ ...styles.btnSecondary, background: '#E8F5E9', borderColor: '#2E7D32', color: '#2E7D32' }} onClick={saveEdits}>💾 Save</button>
              </>
            )}
            <button style={styles.btnSuccess} onClick={downloadPdf} className="no-print">
              🖨️ Print / Save as PDF
            </button>
          </div>
        </div>

        {!isPaid && (
          <div style={styles.brandBadge}>📌 Free plan — StudyDost branding on PDF · <a href="/plans" style={{ color: '#1565C0' }}>Upgrade ₹199/mo</a></div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        {/* MCQ */}
        {(editMode ? editQuestions : questions).mcq?.length > 0 && (
          <>
            <div style={styles.sectionHead('#1E88E5')}>
              Section A — Multiple Choice Questions (1 mark each)
            </div>
            {(editMode ? editQuestions : questions).mcq.map((q, i) => (
              <div key={i} style={styles.qBlock}>
                <span style={styles.marks}>[{q.marks || 1} mark]</span>
                {editMode ? (
                  <textarea
                    style={{ ...styles.input, minHeight: 60 }}
                    value={editQuestions.mcq[i].q}
                    onChange={e => {
                      const updated = JSON.parse(JSON.stringify(editQuestions));
                      updated.mcq[i].q = e.target.value;
                      setEditQuestions(updated);
                    }}
                  />
                ) : (
                  <div style={styles.qText}><b>Q{i+1}.</b> {q.q}</div>
                )}
                <div style={{ marginTop: 8 }}>
                  {q.options?.map((opt, j) => (
                    <div key={j} style={styles.optText}>{opt}</div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Short Answer */}
        {(editMode ? editQuestions : questions).short?.length > 0 && (
          <>
            <div style={styles.sectionHead('#F57C00')}>
              Section B — Short Answer Questions
            </div>
            {(editMode ? editQuestions : questions).short.map((q, i) => (
              <div key={i} style={styles.qBlock}>
                <span style={styles.marks}>[{q.marks || 2} marks]</span>
                {editMode ? (
                  <textarea
                    style={{ ...styles.input, minHeight: 60 }}
                    value={editQuestions.short[i].q}
                    onChange={e => {
                      const updated = JSON.parse(JSON.stringify(editQuestions));
                      updated.short[i].q = e.target.value;
                      setEditQuestions(updated);
                    }}
                  />
                ) : (
                  <div style={styles.qText}><b>Q{(questions.mcq?.length||0)+i+1}.</b> {q.q}</div>
                )}
              </div>
            ))}
          </>
        )}

        {/* Long Answer */}
        {(editMode ? editQuestions : questions).long?.length > 0 && (
          <>
            <div style={styles.sectionHead('#7B1FA2')}>
              Section C — Long Answer Questions
            </div>
            {(editMode ? editQuestions : questions).long.map((q, i) => (
              <div key={i} style={styles.qBlock}>
                <span style={styles.marks}>[{q.marks || 5} marks]</span>
                {editMode ? (
                  <textarea
                    style={{ ...styles.input, minHeight: 80 }}
                    value={editQuestions.long[i].q}
                    onChange={e => {
                      const updated = JSON.parse(JSON.stringify(editQuestions));
                      updated.long[i].q = e.target.value;
                      setEditQuestions(updated);
                    }}
                  />
                ) : (
                  <div style={styles.qText}><b>Q{(questions.mcq?.length||0)+(questions.short?.length||0)+i+1}.</b> {q.q}</div>
                )}
              </div>
            ))}
          </>
        )}

        <div className="no-print" style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <button style={styles.btnSecondary} onClick={() => { setStep('form'); setQuestions(null); }}>
            ← Generate New
          </button>
          <button style={styles.btnSuccess} onClick={downloadPdf}>
            🖨️ Print / Save as PDF
          </button>
        </div>
      </div>
    </div>
  );

  // ── Form ──────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.title}>📚 Homework Generator</div>
        <div style={styles.sub}>AI creates board-aligned questions in seconds</div>

        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.row}>
          <div>
            <label style={styles.label}>Subject *</label>
            <select style={styles.select} value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}>
              <option value="">Select Subject</option>
              {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={styles.label}>Class *</label>
            <select style={styles.select} value={form.classLevel}
              onChange={e => setForm(f => ({ ...f, classLevel: e.target.value }))}>
              <option value="">Select Class</option>
              {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={styles.label}>Chapter / Topic *</label>
          <input style={styles.input} placeholder="e.g. Chapter 6 — Life Processes"
            value={form.chapter}
            onChange={e => setForm(f => ({ ...f, chapter: e.target.value }))} />
        </div>

        <div style={styles.row}>
          <div>
            <label style={styles.label}>Board</label>
            <select style={styles.select} value={form.board}
              onChange={e => setForm(f => ({ ...f, board: e.target.value }))}>
              {BOARDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={styles.label}>Language</label>
            <select style={styles.select} value={form.language}
              onChange={e => setForm(f => ({ ...f, language: e.target.value }))}>
              <option value="ENGLISH">English</option>
              <option value="HINDI">Hindi</option>
              <option value="HINGLISH">Hinglish</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={styles.label}>Question Format</label>
          <div style={styles.templateGrid}>
            {TEMPLATES.map(t => (
              <div key={t.id} style={styles.templateCard(form.template === t.id)}
                onClick={() => setForm(f => ({ ...f, template: t.id }))}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{t.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1565C0' }}>{t.label}</div>
                <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>{t.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {!isPaid && (
          <div style={{ ...styles.brandBadge, display: 'block', marginBottom: 16, textAlign: 'center' }}>
            🆓 Free: 1 homework/day · StudyDost branding on PDF ·{' '}
            <a href="/plans" style={{ color: '#1565C0', fontWeight: 700 }}>Upgrade ₹199/mo to remove branding</a>
          </div>
        )}

        <button style={styles.btnPrimary} onClick={generate}>
          ✨ Generate Homework
        </button>
      </div>
    </div>
  );
}
